import {
  AfterViewInit,
  DestroyRef,
  Directive,
  ElementRef,
  effect,
  inject,
  input,
  signal,
} from '@angular/core'

import { ComponentIdentifierEnum, TemplateConfigInterfaceFromCollection } from '@flowx/core-sdk'
import {
  applyDocumentPreviewSpaceLeft,
  applyWrapperStyles,
  isFlexColumnParent,
  pinGridRowsWhileExpanded,
} from '@flowx/core-theme'

@Directive({
  selector: '[flxWrapperStyle]',
  host: {
    '[class]': 'styleClassName()',
  },
  standalone: true,
})
export class FlxWrapperStyleDirective implements AfterViewInit {
  #elementRef = inject(ElementRef)
  #destroyRef = inject(DestroyRef)
  #displayValue = this.#elementRef.nativeElement.style.display
  #isParentGrid = signal<boolean>(false)
  #isParentFlexColumn = signal<boolean | null>(null)
  flxWrapperStyle = input.required<TemplateConfigInterfaceFromCollection>()
  styleClassName = signal<string>('')

  constructor() {
    effect(() => {
      const templateConfig = this.flxWrapperStyle()
      let adjustedStyles = { ...templateConfig?.displayOptions?.style }
      if (templateConfig?.componentIdentifier === 'CHAT') {
        const displayType = templateConfig?.displayOptions?.flowxProps?.displayTypes?.[0]
        if (displayType === 'FILL') {
          adjustedStyles = { ...adjustedStyles, ...adjustedStyles.fill }
        }
      }
      const isScreenFill = templateConfig.componentIdentifier === ComponentIdentifierEnum.MODAL

      const isParentFlexColumn = this.#isParentFlexColumn()
      const className = [
        applyWrapperStyles(adjustedStyles, isScreenFill, this.#isParentGrid()),
        isParentFlexColumn !== null &&
          applyDocumentPreviewSpaceLeft(
            templateConfig?.componentIdentifier,
            adjustedStyles,
            isParentFlexColumn
          ),
      ]
        .filter(Boolean)
        .join(' ')
      this.styleClassName.set(className)
    })
  }

  ngAfterViewInit(): void {
    const parentElement = this.#elementRef.nativeElement.parentElement
    if (parentElement) {
      Promise.resolve().then(() => {
        const parentDisplay = getComputedStyle(parentElement).display
        this.#isParentGrid.set(parentDisplay === 'grid')
        const templateConfig = this.flxWrapperStyle()
        if (templateConfig?.componentIdentifier === ComponentIdentifierEnum.DOCUMENT_PREVIEW) {
          this.#isParentFlexColumn.set(isFlexColumnParent(parentElement))
        }
        this.#destroyRef.onDestroy(
          pinGridRowsWhileExpanded(
            templateConfig?.componentIdentifier,
            templateConfig?.displayOptions?.style,
            this.#elementRef.nativeElement
          )
        )
      })
    }
  }

  toggleDisplay(isHidden: boolean): void {
    this.#elementRef.nativeElement.style.display = isHidden ? 'none' : this.#displayValue
  }
}
